// @ts-nocheck
/* eslint-disable complexity, react-hooks/exhaustive-deps, local/no-objects-in-deps-list */
import { useEffect, useRef, useState } from "react";

import { shuffle } from "lodash";

import { RichText } from "@atlassiansox/bxp-content-framework";

import {
    AnimatedContainer,
    Column,
    Container,
    Expand,
    LinkButton,
    Row,
    useAnalytics,
    useBreakpoints,
    useRoute
} from "../../components";
import { LANGUAGES } from "../../languages";
import { UiIntegrationModel, UiIntegrationSectionModel } from "../../models";
import {
    BIG_NAV_PX_HEIGHT,
    SMALL_NAV_PX_HEIGHT,
    SUB_NAV_PX_HEIGHT
} from "../../styles";
import { UiIntegration } from "../UiIntegration";

import Navigation from "./components/Navigation";
import SubNav from "./components/SubNav";
import { Featured, Grid, More, Section } from "./UiIntegrationSection.styles";

export type UiIntegrationSectionProps = {
    readonly model: UiIntegrationSectionModel;
    readonly currentPath?: string;
    readonly readMoreLimit?: number;
    readonly randomize?: boolean;
};

export type Category = {
    title: string;
    id: string;
    path: string;
    entryTitle?: string;
};

// Helper function to build categories list
const buildCategories = (
    allIntegrations: UiIntegrationModel[],
    defaultCategory: Category
): Category[] => {
    const categories: Category[] = [defaultCategory];

    allIntegrations
        .filter(({ type }) => type === "uiIntegration")
        .forEach((integration) => {
            integration.content.categories
                ?.filter(({ type }) => type === "integrationCategory")
                .forEach((category) => {
                    if (!categories.find((c) => c.id === category.id)) {
                        categories.push({
                            title: category.content.title,
                            id: category.id,
                            path: category.content.route?.content.path ?? "",
                            entryTitle: category.content.entryTitle
                        });
                    }
                });
        });

    categories.sort((a, b) =>
        b.id === defaultCategory.id ? 1 : a.title.localeCompare(b.title)
    );

    return categories;
};

// Helper function to filter integrations
const filterIntegrations = (
    integrations: UiIntegrationModel[],
    categoryId: string,
    searchTerm: string,
    viewingAll: boolean
): UiIntegrationModel[] => {
    return integrations
        .filter((integration) => {
            const categories =
                integration.content.categories
                    ?.filter(({ type }) => type === "integrationCategory")
                    .map((category) => category.id) ?? [];
            return viewingAll || categories.includes(categoryId);
        })
        .filter((integration) => {
            if (!searchTerm) return true;
            const needle = searchTerm
                .toLowerCase()
                .trim()
                .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const haystack = [
                integration.content.title,
                integration.content.description
            ]
                .join(" ")
                .toLowerCase();
            return haystack.search(needle) !== -1;
        });
};

const UiIntegrationSection = ({
    model,
    currentPath,
    readMoreLimit = 24,
    randomize = true
}: UiIntegrationSectionProps) => {
    const routeFromHook = useRoute();
    const route = currentPath || routeFromHook;
    const [limited, setLimited] = useState(true);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const breakpoints = useBreakpoints();
    const { sendTrackEvent } = useAnalytics();

    const sectionRef = useRef<HTMLElement | null>(null);

    const searchAnalytics = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Initialize with published integrations so cards render immediately on SSR
    // and on first client paint (no empty → populated flicker).
    // useEffect below re-runs to shuffle if randomize=true.
    // Use model.content.allIntegrations directly — the destructured binding
    // is declared later in this function scope (temporal dead zone issue).
    const [shuffledIntegrations, setShuffledIntegrations] = useState<
        UiIntegrationModel[]
    >(() =>
        model.content.allIntegrations.filter(
            ({ type }) => type === "uiIntegration"
        )
    );

    const ANIMATION_CONTAINER_TIME = 600;

    const {
        searchLabel,
        featuredIntegrations,
        featuredIntegrationsTitle,
        filterByText,
        allIntegrations,
        allIntegrationsTitle,
        allIntegrationsRoute,
        viewAllButtonText,
        viewLessButtonText,
        searchResultsHeading,
        noSearchResults
    } = model.content;

    useEffect(() => {
        //filter out any deleted integrations
        const publishedIntegrations = allIntegrations.filter(
            ({ type }) => type === "uiIntegration"
        );

        setShuffledIntegrations(
            randomize ? shuffle(publishedIntegrations) : publishedIntegrations
        );

        return () => {
            if (searchAnalytics.current) {
                clearTimeout(searchAnalytics.current);
            }
        };
    }, [allIntegrations, randomize]);

    //start list of categories
    const defaultCategory = {
        title: allIntegrationsTitle,
        id: "",
        path: allIntegrationsRoute.content.path,
        entryTitle: "All integrations"
    };

    const categories = buildCategories(allIntegrations, defaultCategory);

    //determine current category
    const [filter, setFilter] = useState({
        category:
            categories.find((category) => category.path === route) ??
            defaultCategory,
        search: ""
    });

    const viewingAll = filter.category.id === defaultCategory.id;

    //when filter changes…
    useEffect(() => {
        //update URL when category changes
        if (!languageUrls(filter.category.path).includes(location.pathname)) {
            history.pushState(null, document.title, filter.category.path);
        }

        //scroll to top when searching
        if (filter.search || filter.category !== defaultCategory) {
            if (window && sectionRef.current) {
                const sectionTop =
                    sectionRef.current.getBoundingClientRect().top;
                const top =
                    window.pageYOffset +
                    sectionTop -
                    (breakpoints.includes("lg")
                        ? BIG_NAV_PX_HEIGHT
                        : SMALL_NAV_PX_HEIGHT + SUB_NAV_PX_HEIGHT);
                window.scrollTo({
                    top,
                    behavior: "smooth"
                });
            }
        }

        //wait 2s and fire search analytics
        if (searchAnalytics.current) {
            clearTimeout(searchAnalytics.current);
        }

        if (filter.search) {
            searchAnalytics.current = setTimeout(() => {
                sendTrackEvent("searchedIntegrations", {
                    event: "searched",
                    eventComponent: model.type,
                    eventContainer: "content",
                    label: searchLabel,
                    labelEnglish: "Search all integrations",
                    uuid: model.id
                });
            }, 2000);
        }
    }, [
        filter,
        defaultCategory,
        breakpoints,
        sendTrackEvent,
        model.type,
        model.id,
        searchLabel
    ]);

    //filter by categories and keywords
    const filteredIntegrations = filterIntegrations(
        shuffledIntegrations,
        filter.category.id,
        filter.search,
        viewingAll
    );

    //limit list
    const canBeLimited = filteredIntegrations.length > readMoreLimit;

    const integrations =
        canBeLimited && limited
            ? filteredIntegrations.slice(0, readMoreLimit)
            : filteredIntegrations;

    const toggleViewAll = (wasLimited: boolean) => {
        setLimited(!wasLimited);

        if (wasLimited) {
            // A11Y: When new content is manually loaded, reset focus to first new item
            setTimeout(() => {
                document
                    .querySelector("[data-focused-integration]")
                    ?.querySelector("a")
                    ?.focus();
            }, ANIMATION_CONTAINER_TIME);
        }
    };

    //we have two versions of the nav (desktop in the sidebar, mobile in a fixed nav)
    const navOptions = {
        categories,
        defaultCategory,
        filter,
        model,
        searchLabel,
        setFilter,
        closeMobileNav: () => {
            if (mobileNavOpen) {
                setMobileNavOpen(false);
            }
        }
    };

    return (
        <Section data-testid="integration-section" ref={sectionRef}>
            <SubNav
                isOpen={mobileNavOpen}
                setIsOpen={setMobileNavOpen}
                buttonTestId="integration-menu-button"
                title={
                    <>
                        {filterByText} <strong>{filter.category.title}</strong>
                    </>
                }
            >
                <Navigation isMobile={true} {...navOptions} />
            </SubNav>
            <Container>
                <Row>
                    <Column
                        xs={{ span: "0" }}
                        lg={{ align: "left", span: "3" }}
                    >
                        <Navigation isMobile={false} {...navOptions} />
                    </Column>
                    <Column xs={{ align: "left" }} lg={{ span: "9" }}>
                        <Expand
                            show={
                                !!featuredIntegrations &&
                                viewingAll &&
                                !filter.search
                            }
                            time={500}
                        >
                            <Featured>
                                {featuredIntegrationsTitle && (
                                    <h2 className="h4">
                                        {featuredIntegrationsTitle}
                                    </h2>
                                )}
                                <Row>
                                    {featuredIntegrations
                                        ?.filter(
                                            ({ type }) =>
                                                type === "uiIntegration"
                                        )
                                        .map((featuredIntegration, index) => (
                                            <Column
                                                sm={{ span: "6" }}
                                                lg={{ span: "4" }}
                                                key={index}
                                            >
                                                <UiIntegration
                                                    featured={true}
                                                    model={featuredIntegration}
                                                />
                                            </Column>
                                        ))}
                                </Row>
                            </Featured>
                        </Expand>
                        {!!shuffledIntegrations.length && (
                            <>
                                {filter.category && (
                                    <div role="status">
                                        <h2 className="h4">
                                            {filter.search.length
                                                ? searchResultsHeading
                                                : filter.category.title}
                                            <span>
                                                {filteredIntegrations.length}
                                            </span>
                                        </h2>
                                    </div>
                                )}
                                {!!filter.search.length &&
                                !integrations.length &&
                                noSearchResults ? (
                                    <RichText
                                        className="noSearchResults"
                                        document={noSearchResults}
                                    />
                                ) : (
                                    <Grid>
                                        <AnimatedContainer
                                            time={ANIMATION_CONTAINER_TIME}
                                        >
                                            {integrations.map(
                                                (integration, index) => (
                                                    <UiIntegration
                                                        model={integration}
                                                        key={integration.id}
                                                        data-focused-integration={
                                                            index ===
                                                            readMoreLimit
                                                                ? true
                                                                : undefined
                                                        }
                                                    />
                                                )
                                            )}
                                        </AnimatedContainer>
                                    </Grid>
                                )}
                                {canBeLimited && (
                                    <More>
                                        <LinkButton
                                            appearance="Primary"
                                            onClick={() =>
                                                toggleViewAll(limited)
                                            }
                                            testId="toggle-view-all"
                                        >
                                            {limited
                                                ? viewAllButtonText
                                                : viewLessButtonText}
                                        </LinkButton>
                                    </More>
                                )}
                            </>
                        )}
                    </Column>
                </Row>
            </Container>
        </Section>
    );
};

function languageUrls(url: string): string[] {
    return [url, ...LANGUAGES.map((language) => `/${language.code}${url}`)];
}

export default UiIntegrationSection;
